//Library Imports
import { Popover } from 'antd';
import { RootState } from 'store';
import classNames from 'classnames';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useDrag, DragSourceMonitor } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
//Local Imports
import AddMore from './AddMore';
import useQuery from 'hooks/useQuery';
import { basedOnValues } from '../utils';
import { addActionModal } from '../actions';
import PopoverContent from './PopoverContent';
import { DESIGN } from 'constants/actionTypes';
import { ItemTypes } from 'modules/Common/Components/ItemTypes';
import InfoPopover from 'modules/Common/Components/InfoPopover';
import { ConditionEnum, ControlNameEnum, IListData, WFStatus, WFStatusEnum } from '../interfaces';
//Icons Imports
import DragIcon from 'assets/img/icons/DragIcon.svg';
import StopIcon from '../../../assets/img/icons/stopIcon.svg';
import DelayIcon from '../../../assets/img/icons/delayIconBlack.svg';
import EmailIcon from '../../../assets/img/icons/emailIconBlack.svg';
import ConditionIcon from '../../../assets/img/icons/conditionIconTwo.svg';
import AddToListIcon from '../../../assets/img/icons/addToListIconGrey.svg';
import { ReactComponent as ConnectionIcon } from 'assets/img/icons/connectionBlack.svg';

type Props = {
  id: string;
  data: object;
  end?: boolean;
  wait?: boolean;
  list?: boolean;
  email?: boolean;
  scaling: string;
  disabled: boolean;
  downline?: boolean;
  nodeTitle?: string;
  uperline?: boolean;
  hasChildren: boolean;
  controlName: string;
  controlTitle: string;
};

const ActionNode = (props: Props) => {
  const { controlName, controlTitle, nodeTitle, id, uperline, downline, email, wait, list, disabled, end, data, hasChildren, scaling } = props;
  // Redux state
  const workflowStatus: WFStatus = useSelector((state: RootState) => state.automation.workflow?.detail?.status);
  const errorNodes: string[] = useSelector((state: RootState) => state.automation.workflow?.detail?.errorNodes);
  const IfElseConditions: any[] = useSelector((state: RootState) => state.automation.workflow.flow[id].detail.conditions);
  const newlyAddedNodes: string[] = useSelector((state: RootState) => state.automation.workflow?.detail?.newlyAddedNodes);
  const triggerEmailSent: boolean = useSelector((state: RootState) => state.automation.workflow?.detail?.triggerEmailSent);
  const conditionOperators = useSelector((state: RootState) => state.automation.workflow.flow[id].detail.conditionOperators);
  const errorPublishNodes: string[] = useSelector((state: RootState) => state.automation.workflow?.detail?.errorPublishNodes);

  // Local variables
  const query = useQuery();
  const dispatch = useDispatch();
  const workflowId = query.get('workflow_id');
  const isStatusDraft = workflowStatus && workflowStatus === WFStatusEnum.DRAFT;
  const isStatusActive = workflowStatus && workflowStatus === WFStatusEnum.ACTIVE;
  const isStatusNotActive = workflowStatus && workflowStatus !== WFStatusEnum.ACTIVE;
  const isStatusInactive = workflowStatus && workflowStatus === WFStatusEnum.INACTIVE;
  const redirectionLinks = {
    sent: `/campaigns/editionedit/${data?.versionEmailType === 0 ? '' : 'design/'}${
      data?.campaignId ? `?campaign_id=${data?.campaignId}&` : '?'
    }email_id=${data?.emailId}&workflow_id=${workflowId}`,
    default: `/campaigns/editionedit/design/${data?.campaignId ? `?campaign_id=${data?.campaignId}&` : '?'}email_id=${
      data?.emailId
    }&workflow_id=${workflowId}`,
  };
  const emailRedirectionLink = redirectionLinks[triggerEmailSent ? 'sent' : 'default'];
  let isEmailLinkError = errorNodes.includes(id);
  let nodeErrorText = errorNodes.includes(id) ? 'The link has been removed from the email' : 'The link was removed from the email';
  let isNodeError = errorPublishNodes.filter((e: string) => e === id)[0];

  const [{ isDragging }, dragRef, dragPreview] = useDrag(
    () => ({
      type: ItemTypes.CARD,
      item: { nodeTitle, title: controlTitle, controlName, ...data, nodeId: id },
      collect: (monitor: DragSourceMonitor) => ({
        isDragging: monitor.isDragging(),
      }),
      canDrag: (monitor: DragSourceMonitor) => {
        // status should not equal to empty string and also should not have the "draft" status
        if (isStatusDraft || (isStatusInactive && newlyAddedNodes?.includes(id))) return true;
        if (isStatusActive || isStatusInactive) return false;
        return true;
      },
    }),
    [id, controlName, nodeTitle, newlyAddedNodes, data],
  ); // these dependencies are important.
  // otherwise if elements are dragged then after some time it get memoized and then same element

  useEffect(() => {
    dragPreview(getEmptyImage(), { captureDraggingState: true });
  }, []);

  return (
    <Wrapper
      className={`${list ? 'list-type' : end ? 'end-type' : null} ${scaling === 'scaled' ? 'zoom-out' : 'zoom-in'}`}
      status={workflowStatus}
      controlName={controlName}
      listIsEmpty={data?.list?.length === 0}
    >
      {uperline ? <div className="uperline"></div> : null}
      {data?.list?.length === 0 && <InfoPopover infoText={'Add your list'} errorNode={true} className={'positionStyles'} />}
      {isNodeError ? <InfoPopover infoText={'Fill the mandatory configuration'} width={188} errorNode={true} className={'positionStyles'} /> : <></>}
      {isStatusNotActive ? (
        <Popover
          placement="topRight"
          content={<PopoverContent id={id} controlName={controlName} canCopy={!end} canEdit={!end} />}
          getPopupContainer={() => document.querySelectorAll('.workflow-container')[0] as HTMLElement}
          overlayClassName="workflow-popover workflow-popover-right"
        >
          <div
            className={`action-wrapper ${disabled ? 'disabled' : ''} ${scaling === 'scaled' ? 'zoom-out' : 'zoom-in'} ${
              isNodeError && 'error-wrapper'
            } ${controlName === ControlNameEnum.SEND_EMAIL && 'email-node'} ${wait && data?.radioValue === 2 && 'wait-action-wrapper'}`}
            ref={dragRef}
          >
            {/* disabled toggle class for disable on drag */}
            {/* Node Icons */}
            {email && <img src={EmailIcon} alt="" className="icon email-icon" />}
            {end && <img src={StopIcon} alt="" className="icon" />}
            {wait && data?.radioValue !== 2 ? <img src={DelayIcon} alt="" className="icon delay-icon" /> : null}
            {list && <img src={AddToListIcon} alt="" className="icon list-icon" />}
            <span className={classNames('cover-action', { iconWidth: list || wait })}>
              {/* Node Title */}
              <span className={classNames('action-type', { waitActionType: wait && data?.radioValue === 2 })}>
                {wait && data?.radioValue === 2 ? <img src={DelayIcon} alt="" className="icon delay-icon" /> : null}
                {wait
                  ? data?.radioValue !== 2
                    ? controlTitle?.split('days')[0]?.trim()
                    : 'Wait until: '
                  : email
                  ? controlName?.charAt(0)?.toUpperCase() + controlName?.slice(1)
                  : controlTitle}
              </span>
              {/* Node Content */}
              <span className="action-label">
                {controlName === ControlNameEnum.ADD_LIST ? (
                  <>
                    {': '}
                    {data?.list?.map((li: IListData, index: any) => (
                      <React.Fragment key={index}>
                        <Link
                          className="list-name-styles"
                          to={`/audiences/view?list_id=${li.list_id}&workflow_id=${workflowId}`}
                        >{`${li.list_name}`}</Link>
                        {`${data?.list[data?.list?.length - 1] === li ? '' : ', '}`}
                      </React.Fragment>
                    ))}
                    {data?.list?.length === 0 ? <label className="list_node_error">Select a list</label> : null}
                  </>
                ) : controlName === ControlNameEnum.SEND_EMAIL && data?.emailType !== 'share profile' ? (
                  <>
                    {': '}
                    {/* in order to clear the store for design we need to reset it */}
                    <Link
                      className="list-name-styles"
                      onClick={() => {
                        dispatch({ type: DESIGN.RESET_DESIGN });
                      }}
                      to={emailRedirectionLink}
                    >
                      {data?.emailName}
                    </Link>
                  </>
                ) : controlName === ControlNameEnum.WAIT && !!data?.basedOn && data?.radioValue !== 2 ? (
                  <>
                    {nodeTitle + ': '}
                    {/* in order to clear the store for design we need to reset it */}
                    <span
                      className="list-name-styles"
                      onClick={() => {
                        if (isStatusNotActive) dispatch(addActionModal(true, id, controlName));
                      }}
                    >
                      {Object.values(basedOnValues).find((base) => base.value === data?.basedOn)!.label}
                    </span>
                  </>
                ) : controlName === ControlNameEnum.WAIT && data?.radioValue === 2 ? (
                  // Wait -> Conditions (Option 2)
                  <div className="wait-conditions">
                    <img src={ConditionIcon} alt="" className="condition-img" />
                    <span>
                      {IfElseConditions?.map((condition: any, index: any) => (
                        <React.Fragment key={index}>
                          {index !== 0 ? (
                            <div className="operatorWrapper">
                              <span className="operatorStyles">{conditionOperators[index - 1]}</span>
                            </div>
                          ) : null}
                          {condition?.controlName !== ConditionEnum.CURRENT_TIME && condition?.controlName !== ConditionEnum.CURRENT_DAY ? (
                            <span className="text-wrapper-span">
                              <span className="condition-type">
                                {condition?.controlName}
                                {<>:</>}
                              </span>{' '}
                              {condition?.emailName ? (
                                <span className="email-name-pill">
                                  <label className="email-name-text" title={condition?.emailName}>
                                    {condition?.emailName}
                                  </label>
                                </span>
                              ) : (
                                <span style={{ paddingRight: '8px' }}></span> // Space in between
                              )}
                              {condition.hasOwnProperty('link') && condition?.controlName === ConditionEnum.HAS_CLICKED && (
                                <span style={{ marginRight: '3px' }}>
                                  <ConnectionIcon />
                                </span>
                              )}
                              {isEmailLinkError ? (
                                <span className="condition-label condition-label-error">{nodeErrorText}</span>
                              ) : condition?.controlName === ConditionEnum.HAS_CLICKED ? (
                                condition?.link?.includes('Any Link') ? (
                                  <span className="link-label any-link-label">{condition?.link}</span>
                                ) : (
                                  <a href={condition?.link} target="_blank" rel="noreferrer" className="link-label lineHeight">
                                    {condition?.link}
                                  </a>
                                )
                              ) : (
                                ''
                              )}
                            </span>
                          ) : (
                            <span className="day-and-time">
                              {condition?.controlName} ({condition?.timezone}) {condition?.verb?.toLowerCase()}{' '}
                              {condition?.controlName === ConditionEnum.CURRENT_DAY ? condition?.day : condition?.time}
                            </span>
                          )}
                          <br />
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                ) : (
                  nodeTitle
                )}
              </span>
            </span>
            {(isStatusDraft || (isStatusInactive && newlyAddedNodes?.includes(id))) && (
              <span className="drag-handle">
                <img src={DragIcon} alt="" />
              </span>
            )}
          </div>
        </Popover>
      ) : (
        <div
          className={`action-wrapper ${disabled ? 'disabled' : ''} ${scaling === 'scaled' ? 'zoom-out' : 'zoom-in'} ${
            isNodeError && 'error-wrapper'
          } ${controlName === ControlNameEnum.SEND_EMAIL && 'email-node'} ${wait && data?.radioValue === 2 && 'wait-action-wrapper'}`}
          ref={dragRef}
        >
          {/* disabled toggle class for disable on drag */}
          {/* Node Icons */}
          {email && <img src={EmailIcon} alt="" className="icon email-icon" />}
          {end && <img src={StopIcon} alt="" className="icon" />}
          {wait && data?.radioValue !== 2 ? <img src={DelayIcon} alt="" className="icon delay-icon" /> : null}
          {list && <img src={AddToListIcon} alt="" className="icon list-icon" />}
          <span className={classNames('cover-action', { iconWidth: list || wait })}>
            {/* Node Title */}
            <span className={classNames('action-type', { waitActionType: wait && data?.radioValue === 2 })}>
              {wait && data?.radioValue === 2 ? <img src={DelayIcon} alt="" className="icon delay-icon" /> : null}
              {wait
                ? data?.radioValue !== 2
                  ? controlTitle?.split('days')[0]?.trim()
                  : 'Wait until: '
                : email
                ? controlName?.charAt(0)?.toUpperCase() + controlName?.slice(1)
                : controlTitle}
            </span>
            {/* Node Content */}

            <span className="action-label">
              {controlName === ControlNameEnum.ADD_LIST ? (
                <>
                  {': '}
                  {data?.list?.map((li: IListData, index: any) => (
                    <React.Fragment key={index}>
                      <Link
                        className="list-name-styles"
                        to={`/audiences/view?list_id=${li.list_id}&workflow_id=${workflowId}`}
                      >{`${li.list_name}`}</Link>
                      {`${data?.list[data?.list?.length - 1] === li ? '' : ', '}`}
                    </React.Fragment>
                  ))}
                  {data?.list?.length === 0 ? <label className="list_node_error">Select a list</label> : null}
                </>
              ) : controlName === ControlNameEnum.SEND_EMAIL && data?.emailType !== 'share profile' ? (
                <>
                  {': '}
                  {/* in order to clear the store for design we need to reset it */}
                  <Link
                    className="list-name-styles"
                    onClick={() => {
                      dispatch({ type: DESIGN.RESET_DESIGN });
                    }}
                    to={emailRedirectionLink}
                  >
                    {data?.emailName}
                  </Link>
                </>
              ) : controlName === ControlNameEnum.WAIT && !!data?.basedOn && data?.radioValue !== 2 ? (
                <>
                  {nodeTitle + ': '}
                  {/* in order to clear the store for design we need to reset it */}
                  <span
                    className="list-name-styles"
                    onClick={() => {
                      if (isStatusNotActive) dispatch(addActionModal(true, id, controlName));
                    }}
                  >
                    {Object.values(basedOnValues).find((base) => base.value === data?.basedOn)!.label}
                  </span>
                </>
              ) : controlName === ControlNameEnum.WAIT && data?.radioValue === 2 ? (
                // Wait -> Conditions (Option 2)
                <div className="wait-conditions">
                  <img src={ConditionIcon} alt="" className="condition-img" />
                  <span>
                    {IfElseConditions?.map((condition: any, index: any) => (
                      <React.Fragment key={index}>
                        {index !== 0 ? (
                          <div className="operatorWrapper">
                            <span className="operatorStyles">{conditionOperators[index - 1]}</span>
                          </div>
                        ) : null}
                        {condition?.controlName !== ConditionEnum.CURRENT_TIME && condition?.controlName !== ConditionEnum.CURRENT_DAY ? (
                          <span className="text-wrapper-span">
                            <span className="condition-type">
                              {condition?.controlName}
                              {<>:</>}
                            </span>{' '}
                            {condition?.emailName ? (
                              <span className="email-name-pill">
                                <label className="email-name-text" title={condition?.emailName}>
                                  {condition?.emailName}
                                </label>
                              </span>
                            ) : (
                              <span style={{ paddingRight: '8px' }}></span> // Space in between
                            )}
                            {condition.hasOwnProperty('link') && condition?.controlName === ConditionEnum.HAS_CLICKED && (
                              <span style={{ marginRight: '3px' }}>
                                <ConnectionIcon />
                              </span>
                            )}
                            {isEmailLinkError ? (
                              <span className="condition-label condition-label-error">{nodeErrorText}</span>
                            ) : condition?.controlName === ConditionEnum.HAS_CLICKED ? (
                              condition?.link?.includes('Any Link') ? (
                                <span className="link-label any-link-label">{condition?.link}</span>
                              ) : (
                                <a href={condition?.link} target="_blank" rel="noreferrer" className="link-label lineHeight">
                                  {condition?.link}
                                </a>
                              )
                            ) : (
                              ''
                            )}
                          </span>
                        ) : (
                          <span className="day-and-time">
                            {condition?.controlName} ({condition?.timezone}) {condition?.verb?.toLowerCase()}{' '}
                            {condition?.controlName === ConditionEnum.CURRENT_DAY ? condition?.day : condition?.time}
                          </span>
                        )}
                        <br />
                      </React.Fragment>
                    ))}
                  </span>
                </div>
              ) : (
                nodeTitle
              )}
            </span>
          </span>
          {(isStatusDraft || (isStatusInactive && newlyAddedNodes?.includes(id))) && (
            <span className="drag-handle">
              <img src={DragIcon} alt="" />
            </span>
          )}
        </div>
      )}
      {downline && (
        <>
          <div className="downline"></div>
          <AddMore id={id} disabled={disabled} hasChildren={hasChildren} />
        </>
      )}
    </Wrapper>
  );
};

export default ActionNode;

type WrapperProps = {
  status?: any;
  controlName?: string;
  listIsEmpty?: boolean;
};

const Wrapper = styled.div<WrapperProps>`
  display: block;
  text-align: center;
  clear: both;
  margin: 0 30px;
  .action-wrapper {
    background-color: #ffffff;
    border-radius: 8px;
    padding: 6px 14px;
    border: 1px solid
      ${(props) => (props.status === WFStatusEnum.INACTIVE && props.controlName === ControlNameEnum.SEND_EMAIL ? '#1ec252' : '#7e7e7e')};
    display: inline-block;
    margin: 0 auto;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    -khtml-user-select: none;
    -moz-user-select: none;
    -ms-user-select: none;
    user-select: none;
    position: relative;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      border-radius: 6px;
      padding: 6px 10px;
    }
    &.disabled {
      opacity: 0.3;
      pointer-events: none;
    }
    &.zoom-out {
      border: 2px solid #7e7e7e;
    }
    &:hover .drag-handle {
      opacity: 1;
    }
    &.wait-action-wrapper {
      padding: 16px 15px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 12px 11px;
      }
    }
    .email-name-pill {
      padding: 1px 5px;
      background: #e6f5ea;
      border: 1px solid #1ec252;
      border-radius: 6px;
      margin-right: 5px;
      display: inline-flex;
    }
    .email-name-text {
      font-family: 'Roboto';
      font-style: normal;
      font-weight: 500;
      font-size: 14px;
      line-height: 15px;
      letter-spacing: -0.4px;
      color: #424b50;
      max-width: 120px;
      overflow: hidden;
      display: inline-block;
      white-space: nowrap;
      text-overflow: ellipsis;
      vertical-align: middle;
      padding: 3px 0;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
        padding: 1px 0;
      }
    }
    .wait-conditions {
      background-color: #f5f5f5;
      border: 1px solid #929292;
      border-radius: 6px;
      padding: 16px;
      display: flex;
      align-items: flex-start;
      .condition-img {
        margin-right: 8px;
        margin-top: 5px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 20px;
          height: 12px;
          margin-top: 4px;
        }
      }
    }
    .operatorWrapper {
      text-align: center;
      margin-top: 16px;
      margin-bottom: 14px;
    }
    .operatorStyles {
      display: inline-block;
      min-width: 35px;
      background: #ffffff;
      border: 1px solid #1ec252;
      border-radius: 6px;
      padding: 8px 5px;
      margin-right: 5px;
      gap: 10px;

      font-family: 'Roboto';
      font-style: normal;
      font-weight: 600;
      font-size: 12px;
      line-height: 10px;
      letter-spacing: -0.4px;
      color: #000000 !important;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 10px;
      }
    }
    .contition-label {
      color: #7e7e7e;
      font-size: 14px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      white-space: nowrap;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
    }
    .text-wrapper-span {
      display: inline;
      max-width: 500px;
      white-space: normal;
      text-align: left;
      overflow: hidden;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        max-width: 450px;
      }
    }
    .link-label {
      color: #8aa4e5;
      text-decoration: underline;
      text-decoration-color: #8aa4e5;
      font-size: 14px;
      font-weight: 500;
      font-family: 'Roboto', sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
    }
    .any-link-label {
      text-decoration: none;
      color: #8aa4e5 !important;
    }
    .condition-label-error {
      color: #e41010;
    }
    .lineHeight {
      line-height: 15px;
    }
    .day-and-time {
      display: inline-block;
      font-family: 'Roboto';
      font-size: 16px;
      font-weight: 500;
      line-height: 20px;
      letter-spacing: -0.3999999761581421px;
      color: #424b50;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
    }
    .icon {
      width: 20px;
      height: 20px;
      vertical-align: top;
      margin-right: 8px;
      float: left;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        width: 16px;
        height: 16px;
      }
      &.list-icon {
        width: 16px;
        height: 13px;
        margin-top: 6px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 14px;
          height: 11px;
          margin-top: 3px;
        }
      }
      &.delay-icon {
        margin-top: 2px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0px;
        }
      }
      &.email-icon {
        margin-top: 2px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          margin-top: 0px;
        }
      }
    }
    .drag-handle {
      position: absolute;
      right: -36px;
      top: 50%;
      transform: translateY(-50%);
      cursor: grab;
      transition: 0.5s;
      opacity: 0;
      img {
        width: 16px;
        height: 16px;
      }
    }
    span {
      color: #001823;
      font-size: 16px;
      font-family: 'Roboto', sans-serif;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        font-size: 12px;
      }
      &.cover-action {
        display: inline-block;
        max-width: 500px;
        white-space: normal;
        text-align: left;
        float: left;
        overflow: hidden;
      }
      &.action-type {
        font-weight: 700;
        color: #424b50;
      }
      &.waitActionType {
        display: inline-block;
        margin-bottom: 8px;
      }
      &.condition-type {
        font-weight: 700;
        color: #424b50;
      }
      &.action-label {
        font-weight: 500;
        :hover {
          cursor: pointer;
        }
      }
    }
  }
  .error-wrapper {
    border-color: #e41010;
    background-color: #ffe7e7;
  }
  .email-node {
    border-color: #8aa4e5;
    background-color: #e7edff;
  }
  .list-name-styles {
    color: #1ec252 !important;
    text-decoration: underline;
    text-decoration-color: #1ec252;
  }
  .downline {
    background-color: #979797;
    width: 2px;
    height: 25px;
    margin: -6px auto 0 auto;
  }
  .uperline {
    background-color: #979797;
    width: 2px;
    height: 30px;
    margin: 0 auto;
    position: relative;
    z-index: 1;
    &:after {
      content: '';
      width: 8px;
      height: 8px;
      border-radius: 10px;
      background-color: #979797;
      display: inline-block;
      position: absolute;
      bottom: -3px;
      left: -3px;
    }
  }
  &.list-type {
    .action-wrapper {
      border-color: ${(props) => (props.listIsEmpty ? '#E41010' : '#1ec252')};
      background-color: ${(props) => (props.listIsEmpty ? '#ffe7e7' : '#e6f5ea')};
      padding: 6px 18px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 7px 12px;
      }
      span {
        color: #7e7e7e;
        font-size: 16px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
      }
    }
    &.zoom-out {
      .action-wrapper {
        border: 2px solid #1ec252;
      }
    }
  }
  &.end-type {
    .action-wrapper {
      border-color: #e41010;
      background-color: #ffe7e7;
      padding: 12px 18px;
      @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
        padding: 8px 12px;
      }
      span {
        color: #7e7e7e;
        font-size: 14px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          font-size: 11px;
        }
      }
    }
    &.zoom-out {
      .action-wrapper {
        border: 2px solid #e41010;
      }
    }
  }
  .nodeDisable {
    opacity: 0.5;
    pointer-events: none;
  }
  .fade {
    opacity: 0.5;
  }
  .list_node_error {
    font-family: 'Roboto';
    font-size: 14px;
    font-weight: 500;
    line-height: 20px;
    letter-spacing: -0.3499999940395355px;
    color: #d22121;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      font-size: 13px;
    }
  }
  .positionStyles {
    position: absolute;
    margin-left: -32px;
    margin-top: 14px;
    pointer-events: auto;
    @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
      margin-top: 8px;
    }
  }
`;
